import { selectBreadcrumbs, selectIsFilterActive, selectSearchQuery } from "~/Search/redux/searchSelectors";
import { SearchPageType, useSearchPageType } from "~/Search/useSearchPageType";
import { type BreadcrumbData, BreadcrumbType } from "~/utils/models";
import { clearSearchValueAction, removeBreadcrumb, toggleCampaignOnlyResultsThunk } from "~/Search/redux/actions";
import { NumberFormatter as NF } from "~/utils/NumberFormatter";
import { type ReactNode, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

export interface AppliedFilterData {
  key: string;
  onClick: () => void;
  value: ReactNode;
}

const formatter = NF.getFormatter();

export const useSelectAppliedFilters = () => {
  const { t } = useTranslation();
  const breadcrumbs = useSelector(selectBreadcrumbs);

  const searchValue = useSelector(selectSearchQuery);

  const searchPageType = useSearchPageType();

  const dispatch = useDispatch();
  const { onRemoveBreadcrumb, clearSearchValue, clearSalesFilter } = useMemo(
    () => ({
      onRemoveBreadcrumb: (breadcrumb: BreadcrumbData) => dispatch(removeBreadcrumb(breadcrumb)),
      clearSearchValue: () => dispatch(clearSearchValueAction(searchPageType)),
      clearSalesFilter: () => dispatch(toggleCampaignOnlyResultsThunk(false)),
    }),
    [searchPageType],
  );

  const appliedFilters: AppliedFilterData[] = [];

  breadcrumbs?.forEach((breadcrumb) => {
    if (breadcrumb.type === BreadcrumbType.FACET) {
      appliedFilters.push({
        key: `${breadcrumb.facetCode}-${breadcrumb.facetValueCode}`,
        onClick: () => onRemoveBreadcrumb(breadcrumb),
        value: breadcrumb.name,
      });
    } else {
      appliedFilters.push({
        key: `${breadcrumb.code}-${breadcrumb.valueType}`,
        onClick: () => onRemoveBreadcrumb(breadcrumb),
        value: (
          <span>
            <Trans i18nKey={`solrtechfilter.name.${breadcrumb.code.replace("imperial", "")}translation`}>Name</Trans>
            <span> </span>
            <span>{t(`searchPage.refinement.value.label.${breadcrumb.valueType.toLowerCase()}`).toLowerCase()}</span>
            {": "}
            <span>{`${formatter.format(breadcrumb.value)} ${breadcrumb.uom}`}</span>
          </span>
        ),
      });
    }
  });

  const isSalesCampaignPage = searchPageType === SearchPageType.Campaign;
  if (isSalesCampaignPage && searchValue !== "") {
    appliedFilters.push({
      key: searchValue,
      onClick: () => clearSearchValue(),
      value: t("feature.salesCampaign.facet.searchQuery", { query: searchValue }),
    });
  }

  const isSalesFilterActive = useSelector(selectIsFilterActive);
  if (!isSalesCampaignPage && isSalesFilterActive) {
    appliedFilters.push({
      key: searchValue,
      onClick: () => clearSalesFilter(),
      value: t("feature.salesCampaign.specialDealPrice"),
    });
  }

  return appliedFilters;
};
